import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";

import { TracesNavButtonMenu } from "./TracesNavButtonMenu";

import { BasicTooltip } from "@/components/ui/Tooltip";
import { SESSION_STORAGE } from "@/constants";
import { useNavigateWithSearch } from "@/hooks";
import { stripTracesDrawerIntent } from "@/utils/tracesUrlParams";
import {
  isWorkspaceTracesAnalyticsPathname,
  isWorkspaceTracesSearchPathname,
} from "@/utils/workspacePathMatchers";

interface TracesNavButtonProps {
  isCollapsed?: boolean;
  isTracesOpen?: boolean;
  onToggleTraces?: () => void;
  tracesTooltip?: string;
  showTracesCaret?: boolean;
}

export const TracesNavButton = ({
  isCollapsed = false,
  isTracesOpen = false,
  onToggleTraces,
  tracesTooltip = "Traces",
  showTracesCaret = true,
}: TracesNavButtonProps) => {
  const { workspaceId } = useParams();
  const location = useLocation();
  const navigate = useNavigateWithSearch();
  const [submenuOpen, setSubmenuOpen] = useState(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const openSubmenu = useCallback(() => {
    clearCloseTimer();
    setSubmenuOpen(true);
  }, [clearCloseTimer]);

  const scheduleCloseSubmenu = useCallback(() => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => setSubmenuOpen(false), 150);
  }, [clearCloseTimer]);

  useEffect(() => () => clearCloseTimer(), [clearCloseTimer]);

  const tracesSearchPath = workspaceId ? `/workspace/${workspaceId}/traces-search` : null;
  const tracesAnalyticsPath = workspaceId ? `/workspace/${workspaceId}/traces-analytics` : null;
  const isTracesSearchPageActive = isWorkspaceTracesSearchPathname(location.pathname);
  const isTracesAnalyticsPageActive = isWorkspaceTracesAnalyticsPathname(location.pathname);
  const isOnTracesPage = isTracesSearchPageActive || isTracesAnalyticsPageActive;
  const isTracesDrawerActive =
    Boolean(isTracesOpen) && !isTracesSearchPageActive && !isTracesAnalyticsPageActive;
  const isTracesSectionActive =
    isTracesSearchPageActive || isTracesAnalyticsPageActive || Boolean(isTracesOpen);

  const storeReturnUrlIfNeeded = useCallback(() => {
    if (isOnTracesPage) {
      return;
    }
    sessionStorage.setItem(
      SESSION_STORAGE.TRACES_RETURN_URL,
      `${location.pathname}${location.search}${location.hash}`,
    );
  }, [isOnTracesPage, location.hash, location.pathname, location.search]);

  const handleTracesSearchClick = useCallback(() => {
    if (!tracesSearchPath || isTracesSearchPageActive) {
      setSubmenuOpen(false);
      return;
    }
    storeReturnUrlIfNeeded();
    navigate(tracesSearchPath, { search: stripTracesDrawerIntent(location.search) });
    setSubmenuOpen(false);
  }, [
    isTracesSearchPageActive,
    location.search,
    navigate,
    storeReturnUrlIfNeeded,
    tracesSearchPath,
  ]);

  const handleTracesAnalyticsClick = useCallback(() => {
    if (!tracesAnalyticsPath || isTracesAnalyticsPageActive) {
      setSubmenuOpen(false);
      return;
    }
    storeReturnUrlIfNeeded();
    navigate(tracesAnalyticsPath, { search: stripTracesDrawerIntent(location.search) });
    setSubmenuOpen(false);
  }, [
    isTracesAnalyticsPageActive,
    location.search,
    navigate,
    storeReturnUrlIfNeeded,
    tracesAnalyticsPath,
  ]);

  const buttonVariant = isTracesSectionActive ? "blue" : "ghost";
  const caretRotationClass = isTracesDrawerActive ? "rotate-180 text-blue-600" : "text-gray-400";

  const handlePrimaryClick = useCallback(() => {
    if (isOnTracesPage) {
      return;
    }
    onToggleTraces?.();
  }, [isOnTracesPage, onToggleTraces]);

  return (
    <BasicTooltip shouldShow={isCollapsed} content={tracesTooltip} side="top">
      <span>
        <TracesNavButtonMenu
          isCollapsed={isCollapsed}
          submenuOpen={submenuOpen}
          onSubmenuOpenChange={setSubmenuOpen}
          onSubmenuOpen={openSubmenu}
          onSubmenuClose={scheduleCloseSubmenu}
          buttonVariant={buttonVariant}
          caretRotationClass={caretRotationClass}
          isTracesSectionActive={isTracesSectionActive}
          showTracesCaret={showTracesCaret}
          isTracesSearchPageActive={isTracesSearchPageActive}
          isTracesAnalyticsPageActive={isTracesAnalyticsPageActive}
          onPrimaryClick={handlePrimaryClick}
          onTracesSearchClick={handleTracesSearchClick}
          onTracesAnalyticsClick={handleTracesAnalyticsClick}
        />
      </span>
    </BasicTooltip>
  );
};
